import { createSelector } from "@reduxjs/toolkit";
import { getCampaignsByArticleId } from "../../models/menu/Menu";
import OrderArticle from "../../models/order/OrderArticle";
import Campaign, { CampaignPricingRule, XPerGroupForYCampaign } from "../../models/campaigns/Campaign";
import calculateBuyXPayYCampaign from "../../order/campaigns/calculateBuyXPayYCampaign";
import calculateXForYCampaign from "../../order/campaigns/calculateXForYCampaign";
import calculateXthItemYPercentageOffCampaign from "../../order/campaigns/calculateXthItemYPercentageOffCampaign";
import { selectCampaignsMap } from "./selectCampaignsMap";
import { selectArticleCampaignsMap } from "./selectArticleCampaignsMap";
import { RootState } from "../store";
import calculateXPerGroupForYCampaign from "../../order/campaigns/calculateXPerGroupForYCampaign.ts";
import _ from "lodash";
import { selectSalesAreaPriceLineId } from "../../useSalesAreaPriceLineId.ts";
import { selectArticlesMap } from "./selectArticlesMap.ts";
import { selectArticlegroupArticlesMap } from "./selectArticlegroupArticlesMap.ts";
import { selectCampaignVoucherdefsMap } from "./selectCampaignVoucherdefsMap.ts";
import { selectVoucherdefsFromShoppingCartMap } from "./selectVoucherdefsFromShoppingCartMap.ts";

export const selectDiscountsPerCampaign = createSelector(
  [
    (state: RootState) => state.shoppingCart.items,
    selectCampaignsMap,
    selectArticleCampaignsMap,
    selectArticlesMap,
    selectSalesAreaPriceLineId,
    (state: RootState) => state.global.salesarea.custom_data?.boomi?.campaign_discount_product ?? 0,
    selectArticlegroupArticlesMap,
    selectCampaignVoucherdefsMap,
    selectVoucherdefsFromShoppingCartMap,
  ],
  (
    items: OrderArticle[],
    campaignsMap,
    articleCampaignsMap,
    articlesMap,
    priceLineId,
    campaign_discount_product,
    articlegroupArticlesMap,
    campaignVoucherdefsMap,
    voucherdefsMap
  ): { [campaignId: string]: number | undefined } => {
    const discountsPerCampaign: Record<string, number> = {};

    const article = articlesMap[campaign_discount_product];
    if (article == null) {
      return discountsPerCampaign;
    }

    const articleIdsPerCampaign: { [campaignId: string]: string[] | undefined } = {};
    items.forEach((orderArticle) => {
      const campaigns = getCampaignsByArticleId(campaignsMap, articleCampaignsMap, orderArticle.article.id);

      campaigns
        .filter((campaign) => campaign.type !== CampaignPricingRule.X_PER_GROUP_FOR_Y)
        .filter(
          (campaign) =>
            campaignVoucherdefsMap[campaign.id] == null ||
            campaignVoucherdefsMap[campaign.id].some((voucherdefId) => voucherdefsMap[voucherdefId])
        )
        .forEach((campaign) => {
          if (!articleIdsPerCampaign[campaign.id]) {
            articleIdsPerCampaign[campaign.id] = [];
          }
          articleIdsPerCampaign[campaign.id]?.push(orderArticle.article.id);
        });
    });

    const result = findBestPromotionsRecursive(
      _.cloneDeep(items),
      _.values(campaignsMap).filter(
        (campaign) =>
          campaignVoucherdefsMap[campaign.id] == null ||
          campaignVoucherdefsMap[campaign.id].some((voucherdefId) => voucherdefsMap[voucherdefId])
      ),
      0,
      [],
      priceLineId,
      articlegroupArticlesMap,
      _.chain(articleIdsPerCampaign)
        .mapValues((articleIds) =>
          _.chain(articleIds ?? [])
            .map((articleId) => [articleId, true])
            .fromPairs()
            .value()
        )
        .value()
    );

    return _.chain(result.appliedPromotions)
      .groupBy("campaignId")
      .mapValues((discountAmounts) => _.sumBy(discountAmounts, "discountAmount"))
      .value();
  }
);

function findBestPromotionsRecursive(
  orderArticles: OrderArticle[],
  campaigns: Campaign[],
  campaignIndex: number,
  appliedCampaignDiscountsInThisPath: {
    campaignId: number;
    discountAmount: number;
  }[],
  priceLineId: number | null,
  articlegroupArticlesMap: ReturnType<typeof selectArticlegroupArticlesMap>,
  articleIdsPerCampaign: { [campaignId: string]: { [articleId: string]: true | undefined } }
): {
  total_discount: number;
  appliedPromotions: {
    campaignId: number;
    discountAmount: number;
  }[];
  orderArticles: OrderArticle[];
} {
  if (campaignIndex >= campaigns.length) {
    return {
      total_discount: _.sumBy(appliedCampaignDiscountsInThisPath, "discountAmount"),
      appliedPromotions: appliedCampaignDiscountsInThisPath,
      orderArticles,
    };
  }

  const campaign = campaigns[campaignIndex];

  const resultSkippingThisType = findBestPromotionsRecursive(
    _.cloneDeep(orderArticles),
    campaigns,
    campaignIndex + 1,
    _.cloneDeep(appliedCampaignDiscountsInThisPath),
    priceLineId,
    articlegroupArticlesMap,
    articleIdsPerCampaign
  );

  const copyOrderArticles = _.cloneDeep(orderArticles);

  const calculatedDiscountAmount = calculateDiscountAmount(
    campaign,
    copyOrderArticles,
    priceLineId,
    articlegroupArticlesMap,
    articleIdsPerCampaign
  );
  let resultTryingSameType = {
    total_discount: -1,
    appliedPromotions: [...appliedCampaignDiscountsInThisPath],
    orderArticles: orderArticles,
  };
  if (calculatedDiscountAmount && calculatedDiscountAmount > 0) {
    resultTryingSameType = findBestPromotionsRecursive(
      copyOrderArticles,
      campaigns,
      campaignIndex,
      [
        ...appliedCampaignDiscountsInThisPath,
        { campaignId: Number(campaign.id), discountAmount: calculatedDiscountAmount },
      ],
      priceLineId,
      articlegroupArticlesMap,
      articleIdsPerCampaign
    );
  }

  if (resultSkippingThisType.total_discount >= resultTryingSameType.total_discount) {
    return resultSkippingThisType;
  } else {
    return resultTryingSameType;
  }
}
export const selectSumOfDiscountsPerCampaign = createSelector(selectDiscountsPerCampaign, (discountsPerCampaign) => {
  let totalDiscountAmount = 0;
  Object.keys(discountsPerCampaign).forEach((campaignId) => {
    totalDiscountAmount += discountsPerCampaign[campaignId] ?? 0;
  });
  return totalDiscountAmount;
});

function calculateDiscountAmount(
  campaign: Campaign,
  items: OrderArticle[],
  priceLineId: number | null,
  articlegroupArticlesMap: ReturnType<typeof selectArticlegroupArticlesMap>,
  articleIdsPerCampaign: { [campaignId: string]: { [articleId: string]: true | undefined } }
) {
  if (campaign) {
    if (campaign.type === CampaignPricingRule.BUY_X_PAY_Y) {
      return calculateBuyXPayYCampaign(campaign, items, priceLineId, articleIdsPerCampaign[campaign.id] ?? {});
    } else if (campaign.type === CampaignPricingRule.X_FOR_Y) {
      return calculateXForYCampaign(campaign, items, priceLineId, articleIdsPerCampaign[campaign.id] ?? {});
    } else if (
      campaign.type === CampaignPricingRule.XTH_ITEM_Y_PERCENTAGE_OFF ||
      campaign.type === CampaignPricingRule.X_PERCENTAGE_OFF
    ) {
      const { x, y } = ((campaign: Campaign) => {
        if (campaign.type === CampaignPricingRule.XTH_ITEM_Y_PERCENTAGE_OFF) {
          return { x: campaign?.Xs?.[0] ?? 0, y: campaign?.Xs?.[1] ?? 0 };
        } else {
          return { x: 1, y: campaign?.Xs?.[0] ?? 0 };
        }
      })(campaign);

      return calculateXthItemYPercentageOffCampaign(x, y, items, priceLineId, articleIdsPerCampaign[campaign.id] ?? {});
    } else if (isXPerGroupForYCampaign(campaign)) {
      return calculateXPerGroupForYCampaign(campaign, items, priceLineId, articlegroupArticlesMap);
    }
  }
}

function isXPerGroupForYCampaign(campaign: Campaign): campaign is XPerGroupForYCampaign {
  return campaign.type === CampaignPricingRule.X_PER_GROUP_FOR_Y;
}
